<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useLazyLoad } from "@/composables/useLazyLoad";

const props = defineProps({
  leftImage: {
    type: String,
    required: true,
  },
  rightImage: {
    type: String,
    required: true,
  },
  enablePreview: {
    type: Boolean,
    default: true,
  },
  initialPosition: {
    type: Number,
    default: 50,
  },
  rootMargin: {
    type: String,
    default: "100px",
  },
  threshold: {
    type: Number,
    default: 0.01,
  },
});

const { targetRef, isVisible } = useLazyLoad({
  rootMargin: props.rootMargin,
  threshold: props.threshold,
});

const sliderRef = ref(null);
const position = ref(props.initialPosition);
const isDragging = ref(false);
const isHovering = ref(false);
const leftLoaded = ref(false);
const rightLoaded = ref(false);

const imagesReady = computed(() => leftLoaded.value && rightLoaded.value);

const markLeftLoaded = () => {
  leftLoaded.value = true;
};

const markRightLoaded = () => {
  rightLoaded.value = true;
};

// 计算左侧图片的裁剪区域
const { openComparePreview } = useImagePreview();

const handlePreviewClick = (e) => {
  if (!props.enablePreview) return;
  e?.stopPropagation?.();
  if (!isVisible.value || !imagesReady.value) return;
  openComparePreview({
    leftImage: props.leftImage,
    rightImage: props.rightImage,
    initialPosition: position.value,
  });
};

const leftImageStyle = computed(() => ({
  clipPath: `inset(0 ${100 - position.value}% 0 0)`,
}));

const updatePosition = (clientX) => {
  if (!sliderRef.value) return;

  const rect = sliderRef.value.getBoundingClientRect();
  const x = clientX - rect.left;
  const newPos = Math.min(Math.max((x / rect.width) * 100, 0), 100);
  position.value = newPos;
};

const handleMouseMove = (e) => {
  if (!isHovering.value) return;
  updatePosition(e.clientX);
};

const handleMouseEnter = () => {
  isHovering.value = true;
};

const handleMouseLeave = () => {
  isHovering.value = false;
  position.value = 50;
};

const handleTouchStart = (e) => {
  isDragging.value = true;
  updatePosition(e.touches[0].clientX);
};

const handleTouchMove = (e) => {
  if (!isDragging.value) return;
  updatePosition(e.touches[0].clientX);
};

const handleTouchEnd = () => {
  isDragging.value = false;
};

onMounted(() => {
  window.addEventListener("touchmove", handleTouchMove);
  window.addEventListener("touchend", handleTouchEnd);
});

onUnmounted(() => {
  window.removeEventListener("touchmove", handleTouchMove);
  window.removeEventListener("touchend", handleTouchEnd);
});

watch(
  [() => props.leftImage, () => props.rightImage],
  () => {
    leftLoaded.value = false;
    rightLoaded.value = false;
  },
  { immediate: true }
);
</script>

<template>
  <div ref="targetRef" class="relative w-full h-full">
    <div
      v-if="isVisible"
      ref="sliderRef"
      class="relative w-full h-full select-none touch-none group cursor-ew-resize"
      @click="handlePreviewClick"
      @mousemove="handleMouseMove"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @touchstart="handleTouchStart"
    >
      <img
        :src="rightImage"
        class="block w-full h-full object-contain"
        draggable="false"
        alt="After"
        @load="markRightLoaded"
        @error="markRightLoaded"
        v-show="imagesReady"
      />

      <img
        :src="leftImage"
        class="absolute top-0 left-0 w-full h-full object-contain block will-change-[clip-path]"
        :style="leftImageStyle"
        draggable="false"
        alt="Before"
        @load="markLeftLoaded"
        @error="markLeftLoaded"
        v-show="imagesReady"
      />

      <div
        v-show="imagesReady"
        class="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none shadow-[0_0_10px_rgba(0,0,0,0.5)]"
        :style="{ left: `${position}%` }"
      >
        <div
          class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="w-5 h-5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M8.25 4.5l7.5 7.5-7.5 7.5"
            />
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M15.75 4.5L8.25 12l7.5 7.5"
              transform="rotate(180 12 12)"
            />
          </svg>
        </div>
      </div>

      <span
        v-show="imagesReady"
        class="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 text-xs rounded pointer-events-none z-10"
        >Before</span
      >
      <span
        v-show="imagesReady"
        class="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 text-xs rounded pointer-events-none z-10"
        >After</span
      >

      <UIcon
        v-show="imagesReady && enablePreview"
        name="material-symbols-light:zoom-in-rounded"
        class="absolute bottom-4 right-4 w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition pointer-events-none z-10"
      />

      <div
        v-if="!imagesReady"
        class="absolute inset-0 flex flex-col items-center justify-center"
      >
        <div class="flex justify-center items-center py-2">
          <div
            class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
          ></div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="absolute inset-0 flex justify-center items-center h-full w-full"
    >
      <div class="w-full h-full bg-gray-800"></div>
    </div>
  </div>
</template>
